import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@mui/material";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { AppBadge, AppSelect } from "../../../shared/components/ui";
import {
  IconCheck,
  IconHourglass,
  IconWarning,
} from "../../../shared/components/icons";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { listKycSubmissions } from "../api";
import type { KycQueueRow, KycStatus } from "../types";

/** KYC review queue — desktop data grid; NIDA is masked from the server. */

const STATUS_FILTERS: { value: KycStatus | ""; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "correction_requested", label: "Correction requested" },
  { value: "", label: "All statuses" },
];

function statusBadge(status: KycStatus) {
  if (status === "pending")
    return (
      <AppBadge tone="warning">
        <IconWarning size={12} /> Pending
      </AppBadge>
    );
  if (status === "approved")
    return (
      <AppBadge tone="success">
        <IconCheck size={12} /> Approved
      </AppBadge>
    );
  return (
    <AppBadge tone="info">
      <IconHourglass size={12} /> Correction requested
    </AppBadge>
  );
}

function formatSubmitted(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

export function S5KycReviewQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<KycStatus | "">("pending");

  const query = useQuery({
    queryKey: ["kyc-queue", status],
    queryFn: () => listKycSubmissions(status || undefined),
    retry: 1,
    staleTime: 15_000,
  });

  const columns = useMemo<ColumnDef<KycQueueRow, unknown>[]>(
    () => [
      {
        accessorKey: "customer_name",
        header: "Customer",
        cell: (c) => (c.getValue() as string | null) ?? "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (c) => statusBadge(c.getValue() as KycStatus),
      },
      {
        accessorKey: "ward",
        header: "Ward",
        cell: (c) => (c.getValue() as string | null) ?? "—",
        meta: { hideBelow: "md" },
      },
      {
        accessorKey: "national_id",
        header: "NIDA",
        enableSorting: false,
        cell: (c) => (c.getValue() as string | null) ?? "—",
        meta: { hideBelow: "lg" },
      },
      {
        accessorKey: "created_at",
        header: "Submitted",
        cell: (c) => formatSubmitted(c.getValue() as string | null),
        meta: { hideBelow: "md" },
      },
      {
        accessorKey: "id",
        header: "Submission ID",
        meta: { technical: true },
      },
      {
        accessorKey: "lead_id",
        header: "Lead ID",
        meta: { technical: true },
      },
    ],
    [],
  );

  const title =
    status === "pending" && query.data
      ? `KYC review (${query.data.total} pending)`
      : "KYC review";

  if (query.isPending) {
    return (
      <div>
        <PageHeader title="KYC review" subtitle="Review submitted KYC applications." />
        <PageState status="loading" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div>
        <PageHeader title="KYC review" subtitle="Review submitted KYC applications." />
        <PageState
          status="error"
          error={query.error instanceof Error ? query.error.message : "Something went wrong"}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const rows = query.data.submissions;

  return (
    <>
      <PageHeader
        title={title}
        subtitle="Review submitted KYC applications."
        actions={
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
            <AppSelect
              id="kyc-status-filter"
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as KycStatus | "")}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </AppSelect>
          </Stack>
        }
      />

      <PageState
        status={rows.length === 0 ? "empty" : "success"}
        emptyTitle="Queue clear"
        emptyHint={
          status
            ? `No ${STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase()} submissions.`
            : "No submissions."
        }
      >
        <AppDataTable
          data={rows}
          columns={columns}
          onRowClick={(row) => navigate(`/kyc-review/${row.id}`)}
        />
      </PageState>
    </>
  );
}
